import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { ci } from "appworks/debug/ci";
import { ButtonEvent } from "appworks/graphics/elements/button-element";
import { FlexiButton } from "appworks/graphics/elements/flexi-button";
import { FlexiToggle, ToggleEvent } from "appworks/graphics/elements/flexi-toggle";
import { Layers } from "appworks/graphics/layers/layers";
import { Scene } from "appworks/graphics/layers/scene";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import * as TweenJS from "appworks/utils/tween";
import { Signal } from "signals";

export enum LoadType {
    DISABLE_BUTTON = "disableButton",
    LOADING_SCENE = "loadingScene",
    HIDE_BUTTON = "hideButton"
}

export class PromptContent {
    public layer = Layers.get("Prompts");

    public timeOutTime: number;
    public continueButton: FlexiButton;

    public onHide: Signal = new Signal();

    private clickToSkip: boolean;

    private loadRaceType: LoadType = LoadType.DISABLE_BUTTON;

    private buttons = new Map<FlexiButton, () => void>();
    private toggles = new Map<FlexiToggle, (value: boolean) => void>();
    private scene: Scene;

    private continueButtonName: string;
    private textContents: Map<string, string> = new Map<string, string>();
    private buttonContents = new Map<string, () => void>();
    private toggleContents = new Map<string, (value: boolean) => void>();
    private countups = new Map<string, CountupDefinition>();
    private countupContract: Contract<void>;

    private spinnerTween: TweenJS.Tween;

    constructor(private scenes: string[], time?: number, layer?: Layers) {
        if (time == null) {
            this.timeOutTime = PromptComponent.DEFAULT_TIME;
        } else {
            this.timeOutTime = time;
        }

        if (layer) {
            this.layer = layer;
        }

        this.scene = this.layer.getFirstValidScene(this.scenes);

        if (!this.scene) {
            throw new Error("No scene found in entire list for prompt. [" + this.scenes.join(", ") + "]");
        }
    }

    public show(): Contract<void> {

        const setupContents = () => {
            if (this.textContents) {
                this.textContents.forEach((value, index) => {
                    this.populateTextField(index, value);
                });
            }

            if (this.countups) {
                this.countups.forEach((countup: CountupDefinition, contentName: string) => {
                    this.populateTextField(contentName, Services.currency.format(0));
                });
            }

            if (this.continueButtonName) {
                this.continueButton = this.setButton(this.continueButtonName);
            }
        };

        return new Contract<void>((resolve) => {
            this.layer.setScene(this.scene, setupContents).then(() => {
                if (this.buttonContents.size > 0) {
                    this.buttonContents.forEach((value, index, map) => {
                        this.setButton(index, value);
                    });
                }

                if (this.toggleContents.size > 0) {
                    this.toggleContents.forEach((value, index, map) => {
                        this.setToggle(index, value);
                    });
                }

                if (this.countups.size > 0) {
                    const countupContracts: Array<() => Contract<void>> = [];

                    this.countups.forEach((countup: CountupDefinition, contentName: string) => {
                        countupContracts.push(() => this.startCountup(countup, contentName));
                    });

                    this.countupContract = new Parallel<void>(countupContracts).then(() => {
                        this.enableButtons();
                    });
                } else {
                    this.enableButtons();
                }

                if (this.layer.getSprite("spinner")) {
                    this.spinnerTween = new TweenJS.Tween(CenterPivot(this.layer.getSprite("spinner")))
                        .to({ rotation: Math.PI * 2 }, 500)
                        .repeat(Infinity)
                        .start();
                }

                const coverIds = ["cover", "landscape_cover", "portrait_cover"];
                for (const coverId of coverIds) {
                    const cover = this.layer.getSprite(coverId) || this.layer.getShape(coverId);
                    if (cover) {
                        cover.interactive = true;
                        cover.cursor = "default";
                    }
                }

                resolve(null);
            });
        });
    }

    public setText(key: string, value: string) {
        this.textContents.set(key, value);
    }

    public addContinueButton(name: string = "continue"): void {
        this.continueButtonName = name;
    }

    public hasContinueButton() {
/////////////////////////////////////////////////////////
////////////////////////////
/////////////////////////////
/////////
//////////////////
        return this.continueButton;
    }

    public addContinueListener(upHandler: () => void) {
        if (this.hasContinueButton()) {
            this.continueButton.on(ButtonEvent.CLICK, () => {
                Services.sound.event(SoundEvent.prompt_continue_press);
                this.continueButton.setEnabled(false);
                upHandler();
            });
        }
    }

    public hideContinueButton() {
        if (this.hasContinueButton()) {
            this.continueButton.setVisible(false);
        }
    }

    public disableContinueButton() {
        if (this.hasContinueButton()) {
            this.continueButton.setEnabled(false);
        }
    }

    public enableContinueButton() {
        if (this.hasContinueButton()) {
            this.continueButton.setVisible(true);
            this.continueButton.setEnabled(true);
        }
    }

    public addButton(name: string, upHandler?: () => void) {
        this.buttonContents.set(name, upHandler);
    }

    public addToggle(name: string, toggleHandler: (value: boolean) => void) {
        this.toggleContents.set(name, toggleHandler);
    }

    public countupText(name: string, value: number, time: number) {
        this.countups.set(name, new CountupDefinition(value, time));
    }

    public setClickToSkip(clickToSkip: boolean) {
        this.clickToSkip = clickToSkip;
    }

    public hide(): Contract<void> {
        if (!this.onHide) {
            return Contract.empty();
        }

        return new Contract<void>((resolve) => {
            this.layer.defaultScene().then(() => {
                this.onHide.dispatch();
                this.destroy();
                resolve(null);
            });
        });
    }

    public destroy() {
        if (this.spinnerTween) {
            this.spinnerTween.stop();
            delete this.spinnerTween;
        }

        this.buttons.forEach((value, index, map) => {
            index.off(ButtonEvent.CLICK, value);
            index.destroy();
        });

        this.toggles.forEach((value, index, map) => {
            index.off(ToggleEvent.CHANGE, value);
            index.destroy();
        });

        if (this.countupContract) {
            this.countupContract.cancel();
            this.countupContract = null;
        }

        this.buttons = null;
        this.toggles = null;

        this.onHide.dispose();
        this.onHide = null;
    }

    public setLoadType(type: LoadType) {
        this.loadRaceType = type;
    }

    public getLoadRaceType(): LoadType {
        return this.loadRaceType;
    }

    public getClickToSkip(): boolean {
        return this.clickToSkip;
    }

    private enableButtons() {
        this.buttons.forEach((value: any, button: FlexiButton) => {
            button.setEnabled(true);
        });
    }

    private startCountup(countup: CountupDefinition, contentName: string) {
        return new Contract<void>((resolve) => {
            if (countup.value > 0) {
                Services.sound.event(SoundEvent.prompt_countup_start);
                Services.sound.event(SoundEvent.prompt_countup_start_SCENE, this.scene.name);
            }

            const onComplete = () => {
                if (countup.value > 0) {
                    Services.sound.event(SoundEvent.prompt_countup_end);
                    Services.sound.event(SoundEvent.prompt_countup_end_SCENE, this.scene.name);
                }

                this.populateTextField(contentName, Services.currency.format(countup.value));
                resolve(null);
                document.body.removeEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), skipCountup, true);
            };

            const tickObj = { value: 0 };
            const tween = new TweenJS.Tween(tickObj)
                .to({ value: countup.value }, countup.time)
                .onUpdate(() => {
                    this.populateTextField(contentName, Services.currency.format(tickObj.value));
                })
                .onComplete(onComplete);

            const skipCountup = () => {
                tween.stop();
                onComplete();
            };

            document.body.addEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), skipCountup, true);

            tween.start();
        });
    }

    private setButton(name: string, upHandler?: () => void): FlexiButton {
        const button = this.layer.getFlexiButton(name);
        if (button) {
            if (upHandler) {
                const scopedListener = () => upHandler();
                button.on(ButtonEvent.CLICK, scopedListener);
                this.buttons.set(button, scopedListener);
            }

            button.setEnabled(false);
        }

        return button;
    }

    private setToggle(name: string, toggleHandler: (value: boolean) => void) {
        const toggle = this.layer.getFlexiToggle(name);
        if (toggle) {
            toggle.setValue(false);

            const handler = () => {
                const toggleValue = toggle.getValue();
                toggleHandler(toggleValue);
            };

            toggle.on(ToggleEvent.CHANGE, handler);

            this.toggles.set(toggle, handler);
        }
    }

    private populateTextField(id: string, text: string) {
        const textField = this.layer.getFlexiText(id) || this.layer.getBitmapText(id);

        if (textField) {
            textField.setText(text);
        }
    }
}

class CountupDefinition {
    constructor(public value: number, public time: number) {
    }
}
